import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabaseClient';
import { ACCOUNT_SETUP_COMPLETE_EVENT } from '../lib/accountSetup';
import { getCrmDbCapabilities } from '../lib/crmDbSchema';
import { withRetry } from '../lib/supabaseRetry';
import {
  CRM_ACCOUNT_TYPES,
  crmCanAccessPage,
  crmPagesForAccountType,
  isLegacyCrmOwnerEmail,
  normalizeCrmAccountType,
} from '../lib/crmAccountTypes';

const CrmAccountContext = createContext({
  crmAccountType: null,
  businessOwnerId: null,
  isCrmUser: false,
  isCrmOwner: false,
  loading: true,
  canAccessPage: () => false,
  allowedPages: [],
});

const accountCacheKey = (userId) => `crm_account_cache_${userId}`;

// Last successfully resolved account tier, used only when Supabase can't be reached.
function readCachedAccount(userId) {
  try {
    const cached = JSON.parse(localStorage.getItem(accountCacheKey(userId)) || 'null');
    return cached?.type ? cached : null;
  } catch {
    return null;
  }
}

function writeCachedAccount(userId, type, ownerId) {
  try {
    if (type) localStorage.setItem(accountCacheKey(userId), JSON.stringify({ type, ownerId }));
    else localStorage.removeItem(accountCacheKey(userId));
  } catch {
    // storage unavailable — cache is best-effort
  }
}

export function CrmAccountProvider({ children }) {
  const { user } = useAuth();
  const [crmAccountType, setCrmAccountType] = useState(null);
  const [businessOwnerId, setBusinessOwnerId] = useState(null);
  const [loading, setLoading] = useState(false);
  const resolvedUserIdRef = useRef(null);

  useEffect(() => {
    let active = true;

    const resolve = async () => {
      if (!user?.id) {
        resolvedUserIdRef.current = null;
        if (active) {
          setCrmAccountType(null);
          setBusinessOwnerId(null);
          setLoading(false);
        }
        return;
      }

      // Only block the app on the first resolve. Background re-checks (realtime team/profile
      // changes) must not flip `loading`, or the whole CRM unmounts and loses its loaded data.
      const alreadyResolved = resolvedUserIdRef.current === user.id;
      if (!alreadyResolved) setLoading(true);
      try {
        const caps = await getCrmDbCapabilities();
        const emailLower = (user.email || '').toLowerCase().trim();

        let profileType = null;
        if (caps.profilesCrmAccountType) {
          const { data: profile, error: profileErr } = await withRetry(() =>
            supabase
              .from('profiles')
              .select('crm_account_type')
              .eq('id', user.id)
              .maybeSingle(),
          );
          // A failed lookup is not the same as "not a CRM account" — don't demote on errors.
          if (profileErr) throw profileErr;
          profileType = normalizeCrmAccountType(profile?.crm_account_type);
        }

        let crmTeam = null;
        if (caps.teamMembersCrmRole) {
          const { data: teams, error: teamsErr } = await withRetry(() =>
            supabase
              .from('team_members')
              .select('owner_id, crm_role, status')
              .eq('user_id', user.id)
              .in('status', ['accepted', 'pending'])
              .not('crm_role', 'is', null)
              .limit(1),
          );

          if (teamsErr) throw teamsErr;
          crmTeam = teams?.[0];

          if (!crmTeam && emailLower) {
            const { data: byEmail, error: byEmailErr } = await supabase
              .from('team_members')
              .select('id, owner_id, crm_role, status, user_id')
              .eq('email', emailLower)
              .not('crm_role', 'is', null)
              .in('status', ['accepted', 'pending'])
              .maybeSingle();
            if (!byEmailErr && byEmail) {
              crmTeam = byEmail;
              if (!byEmail.user_id) {
                await supabase
                  .from('team_members')
                  .update({
                    user_id: user.id,
                    status: 'accepted',
                    joined_at: new Date().toISOString(),
                  })
                  .eq('id', byEmail.id);
              }
            }
          }
        }

        const teamType = normalizeCrmAccountType(crmTeam?.crm_role);

        let finalType = null;
        let ownerId = user.id;

        if (profileType === CRM_ACCOUNT_TYPES.FULL) {
          finalType = CRM_ACCOUNT_TYPES.FULL;
          ownerId = user.id;
        } else if (crmTeam && teamType) {
          finalType = teamType;
          ownerId = crmTeam.owner_id;
        } else if (isLegacyCrmOwnerEmail(user.email)) {
          finalType = CRM_ACCOUNT_TYPES.FULL;
          ownerId = user.id;
        }

        writeCachedAccount(user.id, finalType, ownerId);
        if (active) {
          resolvedUserIdRef.current = user.id;
          setCrmAccountType(finalType);
          setBusinessOwnerId(finalType ? ownerId : null);
        }
      } catch (err) {
        console.error('[CrmAccountContext]', err);
        // Keep an already-resolved account when a background re-check fails; on a failed first
        // load fall back to the last account this browser resolved for the user.
        if (active && !alreadyResolved) {
          const cached = readCachedAccount(user.id);
          if (cached) {
            setCrmAccountType(cached.type);
            setBusinessOwnerId(cached.ownerId || user.id);
          } else if (isLegacyCrmOwnerEmail(user.email)) {
            setCrmAccountType(CRM_ACCOUNT_TYPES.FULL);
            setBusinessOwnerId(user.id);
          } else {
            setCrmAccountType(null);
            setBusinessOwnerId(null);
          }
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    resolve();

    const onSetupComplete = () => resolve();
    window.addEventListener(ACCOUNT_SETUP_COMPLETE_EVENT, onSetupComplete);

    // Signed-out visitors (the public landing page) have nothing to listen for, so they don't open a
    // realtime connection at all.
    const channel = user?.id
      ? supabase
        .channel(`crm-account-${user.id}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'team_members', filter: `user_id=eq.${user.id}` },
          () => resolve(),
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
          () => resolve(),
        )
        .subscribe()
      : null;

    return () => {
      active = false;
      window.removeEventListener(ACCOUNT_SETUP_COMPLETE_EVENT, onSetupComplete);
      if (channel) supabase.removeChannel(channel);
    };
  }, [user?.id, user?.email]);

  const value = useMemo(() => {
    const isCrmUser = crmAccountType != null;
    const isCrmOwner = isCrmUser && crmAccountType === CRM_ACCOUNT_TYPES.FULL && businessOwnerId === user?.id;
    const allowedPages = isCrmUser ? crmPagesForAccountType(crmAccountType) : [];
    return {
      crmAccountType,
      businessOwnerId,
      isCrmUser,
      isCrmOwner,
      loading,
      allowedPages,
      canAccessPage: (pageId) => (isCrmUser ? crmCanAccessPage(crmAccountType, pageId) : false),
      dataUserId: businessOwnerId || user?.id || null,
    };
  }, [crmAccountType, businessOwnerId, loading, user?.id]);

  return (
    <CrmAccountContext.Provider value={value}>
      {children}
    </CrmAccountContext.Provider>
  );
}

export const useCrmAccount = () => useContext(CrmAccountContext);
